/**
 * TanStack hook for the tenant's credit state (Story 16.4) — query key
 * `['credit', 'status', tenantId]` (the tenant scopes the cache, so a re-login
 * as another tenant never shows the previous tenant's state), 60s staleTime,
 * polled every 60s (on /chat the window never loses focus, so focus refetch
 * alone would miss a crossing mid-session) and refetched on window focus. No
 * retry — an error just hides the banner.
 */

import { useQuery } from '@tanstack/react-query';
import { fetchCreditStatus, type CreditStatus } from '@/lib/api/credit-status';
import { useAuth } from '@/lib/contexts/AuthContext';

/** Key prefix — invalidate `['credit', 'status']` to refetch every tenant's entry. */
export const CREDIT_STATUS_QUERY_KEY = ['credit', 'status'] as const;

export function useCreditStatus() {
  const { tenant } = useAuth();
  const tenantId = tenant?.id ?? null;
  return useQuery<CreditStatus, Error>({
    queryKey: [...CREDIT_STATUS_QUERY_KEY, tenantId],
    queryFn: ({ signal }) => fetchCreditStatus(signal),
    enabled: tenantId !== null,
    staleTime: 60_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    retry: false,
  });
}
